import { useCallback } from 'react';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useCommentMarkerSyncStatusMigrationMutation } from '../../shared/hooks';
import { useSaveExportXmpMetadataToCompositionMutation } from '../gql';

type SaveExportMetadataArgs = {
  /** The composition ID to save metadata to. */
  compositionId: string;
  /** The Frame.io asset created from this composition export. */
  assetId: string;
  /** The Frame.io project the asset belongs to. */
  projectId: string;
  /** Optional version stack id (unused in AE today). */
  versionId?: string;
  /**
   * Render Queue time span on the composition (µs), persisted for marker sync
   * (comment timestamps are asset-relative; this offsets them back to comp time).
   */
  workAreaStart?: number | null;
  workAreaDuration?: number | null;
};

/**
 * Saves Frame.io ↔ composition XMP on the host. After success, re-enables comment
 * marker sync for that asset and composition (parity with Premiere export metadata).
 */
export const useSaveExportMetadata = () => {
  const [saveExportXmpMetadata] = useSaveExportXmpMetadataToCompositionMutation(
    {
      context: { resolveWithUXP: true },
    }
  );
  const activeAccountId = useActiveAccountId();
  const { setCommentMarkerSyncStatusForDocument } =
    useCommentMarkerSyncStatusMigrationMutation();

  const handleSave = useCallback(
    async ({
      compositionId,
      assetId,
      projectId,
      versionId,
      workAreaStart,
      workAreaDuration,
    }: SaveExportMetadataArgs) =>
      saveExportXmpMetadata({
        variables: {
          compositionId,
          xmpMetadata: {
            assetId,
            projectId,
            versionId,
            accountId: activeAccountId,
            workAreaStart,
            workAreaDuration,
          },
        },
        refetchQueries: ['GetAllCompositionExportsXmpMetadata'],
        onCompleted: async () => {
          await setCommentMarkerSyncStatusForDocument({
            assetId,
            documentId: compositionId,
            status: 'ENABLED',
          });
        },
      }),
    [
      activeAccountId,
      saveExportXmpMetadata,
      setCommentMarkerSyncStatusForDocument,
    ]
  );

  return handleSave;
};
